import { useState, type FormEvent } from "react";
import type { AddressFields } from "../../api/account";
import { Button, Input } from "../ui";

export const EMPTY_ADDRESS: AddressFields = { recipient_name: "", phone: "", street: "", barangay: "", city: "", province: "", postal_code: "" };

const FIELDS: { key: keyof AddressFields; label: string; autoComplete: string; placeholder?: string; wide?: boolean; optional?: boolean }[] = [
  { key: "recipient_name", label: "Full name", autoComplete: "name" },
  { key: "phone", label: "Mobile number", autoComplete: "tel", placeholder: "0917 000 0000" },
  { key: "street", label: "House number, street, building", autoComplete: "address-line1", wide: true },
  { key: "barangay", label: "Barangay", autoComplete: "address-line2" },
  { key: "city", label: "City or municipality", autoComplete: "address-level2" },
  { key: "province", label: "Province", autoComplete: "address-level1" },
  { key: "postal_code", label: "Postal code", autoComplete: "postal-code", optional: true },
];

export interface AddressFormProps {
  initial: AddressFields;
  onSave: (address: AddressFields) => void;
  /** Leave out where there's nothing to go back to. */
  onCancel?: () => void;
  saving: boolean;
  /** Messages from the server, by field. */
  errors: Record<string, string[] | undefined>;
  /** A failure that isn't about one field. */
  error?: string;
}

/** A Philippine delivery address. Everything but the postal code is needed to deliver. */
export function AddressForm({ initial, onSave, onCancel, saving, errors, error }: AddressFormProps) {
  const [address, setAddress] = useState(initial);
  const [missing, setMissing] = useState<Set<string>>(new Set());

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const blank = new Set(FIELDS.filter((field) => !field.optional && !address[field.key].trim()).map((field) => field.key));
    setMissing(blank);
    if (blank.size === 0) onSave(address);
  };

  return (
    <form onSubmit={submit} noValidate className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
      {FIELDS.map((field) => (
        <Input
          key={field.key}
          label={field.optional ? `${field.label} (optional)` : field.label}
          autoComplete={field.autoComplete}
          placeholder={field.placeholder}
          inputMode={field.key === "phone" ? "tel" : field.key === "postal_code" ? "numeric" : undefined}
          mono={field.key === "phone"}
          value={address[field.key]}
          error={missing.has(field.key) ? `Enter the ${field.label.toLowerCase()}` : errors[field.key]?.[0]}
          onChange={(event) => {
            setAddress({ ...address, [field.key]: event.target.value });
            if (missing.has(field.key)) setMissing(new Set([...missing].filter((key) => key !== field.key)));
          }}
          className={field.wide ? "sm:col-span-2" : undefined}
        />
      ))}
      {error && (
        <p role="alert" className="m-0 rounded-md bg-danger-100 px-4 py-3 type-small text-danger-700 sm:col-span-2">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-3 sm:col-span-2">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save address"}
        </Button>
        {onCancel && (
          <Button type="button" variant="quiet" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
